import { apiClient } from './client';
import type { Listing, ListingListItem, AuditLogEntry, ListingStats } from '../types';
import type { PaginatedResponse } from '../types/api';

export interface ListingQueryParams {
  page?: number;
  per_page?: number;
  state?: string;
  states?: string[];
  state_mode?: 'include' | 'exclude';
  zipcode?: string;
  platform?: string;
  search_text?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  needs_attention?: boolean;
  has_adresse?: boolean;
  has_haus?: boolean;
  has_einheit?: boolean;
  has_vermieter?: boolean;
  has_verfahren?: boolean;
  vermieter_ids?: number[];
  // Location
  address_search?: string;
  zipcode_search?: string;
  city_search?: string;
  // Multi-select property
  platforms?: string[];
  listing_types?: string[];
  // Numeric ranges
  price_min?: number;
  price_max?: number;
  rating_min?: number;
  rating_max?: number;
  guests_min?: number;
  guests_max?: number;
  bedrooms_min?: number;
  bedrooms_max?: number;
  size_min?: number;
  size_max?: number;
  // Booked nights
  booked_from?: string;
  booked_to?: string;
  booked_min_nights?: number;
  booked_max_nights?: number;
  include_hidden?: boolean;
  duplicates_only?: boolean;
  is_online?: boolean;
}

export type ListingListResponse = PaginatedResponse<ListingListItem>;

export interface ListingUpdateData {
  address?: string;
  baugebiet?: string;
  city?: string;
  zipcode?: string;
  address_verified?: boolean;
  registration_number?: string;
  notes?: string;
  responsible_person?: string;
  host_name?: string;
  host_type?: string;
  listing_type?: string;
  vermieter_id?: number | null;
  adresse_id?: number | null;
  einheit_id?: number | null;
  haus_id?: number | null;
}

export const listingsApi = {
  getListings: async (params: ListingQueryParams = {}): Promise<ListingListResponse> => {
    const { data } = await apiClient.get('/listings/', { params });
    return data;
  },

  getListing: async (id: number): Promise<Listing> => {
    const { data } = await apiClient.get(`/listings/${id}`);
    return data;
  },

  updateListing: async (id: number, updates: ListingUpdateData): Promise<Listing> => {
    const { data } = await apiClient.patch(`/listings/${id}`, updates);
    return data;
  },

  changeState: async (id: number, newState: string, comment?: string): Promise<Listing> => {
    const { data } = await apiClient.patch(`/listings/${id}/state`, {
      new_state: newState,
      comment,
    });
    return data;
  },

  /** Admin-only: per-listing monitoring cron override (null = city default). */
  setMonitorSchedule: async (id: number, detailCronSchedule: string | null): Promise<Listing> => {
    const { data } = await apiClient.put(`/listings/${id}/monitor-schedule`, {
      detail_cron_schedule: detailCronSchedule,
    });
    return data;
  },

  getHistory: async (id: number): Promise<AuditLogEntry[]> => {
    const { data } = await apiClient.get(`/listings/${id}/history`);
    return data;
  },

  getStats: async (): Promise<ListingStats> => {
    const { data } = await apiClient.get('/listings/stats');
    return data;
  },

  getPlatforms: async (): Promise<string[]> => {
    const { data } = await apiClient.get('/listings/platforms');
    return data;
  },

  getListingTypes: async (): Promise<string[]> => {
    const { data } = await apiClient.get('/listings/listing-types');
    return data;
  },

  getStates: async (): Promise<{ key: string; label: string; color: string; recommended_transitions: string[] }[]> => {
    const { data } = await apiClient.get('/listings/states');
    return data;
  },

  scrapeDetails: async (id: number): Promise<{ id: number; detail_scrape_status: string }> => {
    const { data } = await apiClient.post(`/listings/${id}/scrape-details`);
    return data;
  },

  bulkChangeState: async (listingIds: number[], newState: string, comment?: string): Promise<{ updated: number; total: number }> => {
    const { data } = await apiClient.post('/listings/bulk/change-state', {
      listing_ids: listingIds,
      new_state: newState,
      comment,
    });
    return data;
  },

  bulkScrapeDetails: async (listingIds: number[]): Promise<{ queued: number }> => {
    const { data } = await apiClient.post('/listings/bulk/scrape-details', {
      listing_ids: listingIds,
    });
    return data;
  },

  bulkUpdate: async (listingIds: number[], field: string, value: string | number | null): Promise<{ updated: number; total: number }> => {
    const { data } = await apiClient.post('/listings/bulk/update', {
      listing_ids: listingIds,
      field,
      value,
    });
    return data;
  },

  addPeriodOverride: async (
    id: number,
    payload: { from: string; to: string; category: 'booking' | 'blocker'; comment?: string },
  ): Promise<Listing> => {
    const { data } = await apiClient.post(`/listings/${id}/period-overrides`, payload);
    return data;
  },

  removePeriodOverride: async (id: number, index: number): Promise<Listing> => {
    const { data } = await apiClient.delete(`/listings/${id}/period-overrides/${index}`);
    return data;
  },

  resetPeriodOverride: async (
    id: number,
    payload: { from: string; to: string; comment?: string },
  ): Promise<Listing> => {
    const { data } = await apiClient.post(`/listings/${id}/period-overrides/reset`, payload);
    return data;
  },
};
